import { useMemo, useState } from 'react';
import { buildTicks, vizColor } from '~/lib/viz';

export interface BarSeries {
  key: string;
  label: string;
  color: string;
}

interface GroupedBarChartProps {
  categories: string[];
  series: BarSeries[];
  values: Record<string, number[]>;
  height?: number;
  formatValue: (v: number) => string;
  formatTick: (v: number) => string;
}

const VIEW_W = 640;
const PAD_L = 52;
const PAD_R = 12;
const PAD_T = 16;
const PAD_B = 28;
const BAR_MAX = 24;
const BAR_GAP = 2;

export function GroupedBarChart({ categories, series, values, height = 220, formatValue, formatTick }: GroupedBarChartProps) {
  const [hover, setHover] = useState<{ cat: number; s: number } | null>(null);
  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = height - PAD_T - PAD_B;

  const maxValue = useMemo(() => {
    let max = 0;
    for (const key of Object.keys(values)) {
      for (const v of values[key]) if (v > max) max = v;
    }
    return max;
  }, [values]);

  const ticks = useMemo(() => buildTicks(maxValue, 4), [maxValue]);
  const top = ticks[ticks.length - 1] || 1;
  const yFor = (v: number) => PAD_T + plotH - (v / top) * plotH;

  const groupW = plotW / categories.length;
  const barW = Math.min(BAR_MAX, (groupW - BAR_GAP * (series.length + 1)) / series.length);

  return (
    <div className="relative">
      {series.length >= 2 ? (
        <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5 text-xs text-[color:var(--text-secondary)]">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      ) : null}
      <svg viewBox={`0 0 ${VIEW_W} ${height}`} className="w-full" style={{ height, overflow: 'visible' }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={VIEW_W - PAD_R} y1={yFor(t)} y2={yFor(t)} stroke={vizColor.grid} strokeWidth={1} />
            <text x={PAD_L - 8} y={yFor(t)} textAnchor="end" dominantBaseline="middle" fontSize={10} fill={vizColor.textMuted}>
              {formatTick(t)}
            </text>
          </g>
        ))}
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={PAD_T + plotH} stroke={vizColor.axis} strokeWidth={1} />
        <line x1={PAD_L} x2={VIEW_W - PAD_R} y1={PAD_T + plotH} y2={PAD_T + plotH} stroke={vizColor.axis} strokeWidth={1} />

        {categories.map((cat, ci) => {
          const groupX = PAD_L + ci * groupW;
          const totalBarsW = barW * series.length + BAR_GAP * (series.length - 1);
          const startX = groupX + (groupW - totalBarsW) / 2;
          return (
            <g key={cat}>
              <text x={groupX + groupW / 2} y={height - 8} textAnchor="middle" fontSize={10} fill={vizColor.textMuted}>
                {cat}
              </text>
              {series.map((s, si) => {
                const v = values[s.key][ci];
                const y = yFor(v);
                const x = startX + si * (barW + BAR_GAP);
                const isHover = hover?.cat === ci && hover?.s === si;
                return (
                  <g
                    key={s.key}
                    onPointerEnter={() => setHover({ cat: ci, s: si })}
                    onPointerLeave={() => setHover(null)}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={barW}
                      height={PAD_T + plotH - y}
                      rx={4}
                      fill={s.color}
                      opacity={isHover ? 0.85 : 1}
                    />
                    {isHover ? (
                      <text x={x + barW / 2} y={y - 6} textAnchor="middle" fontSize={10} fill={vizColor.textPrimary} fontWeight={600}>
                        {formatValue(v)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
